import React, { ReactNode, useEffect, useState } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  Fade,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from "@material-ui/core";
import { getRules, getTestResult, upsertRules } from "../../../api/users";
import { isOk } from "../../../result";
import { UserRule, UserRuleTestResult } from "../../../models/UserRule";
import useStyles from "../styles";
import { Alert, AlertTitle } from "@material-ui/lab";
import { ConfirmDialog } from "../../../components/dialogs/ConfirmDialog";
import { StoreMultiSelect } from "../../../components/selects/StoreMultiSelect";
import { Store } from "../../../models/Store";
import { listAll } from "../../../api/storeApi";
import { Link } from "react-router-dom";
import { ExportCsv, ExportPdf } from "@material-table/exporters";
import MaterialTable from "@material-table/core";
import { SummarizeOutlined } from "@mui/icons-material";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";
import { UserOverview } from "../UserOverview";
import { useI18n } from "../../../hooks/useI18n";

interface Props {
  title: string;
  icon: any;
  outCome: string;
}

export const UserRuleContainer = ({ title, icon, outCome }: Props) => {
  const theme = useTheme();
  const styles = useStyles();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useI18n();

  const [state, setState] = useState({
    error: {} as any,
    rules: {} as UserRule[],
    testResults: [] as UserRuleTestResult[],
    upsertSuccess: false,
    showConfirm: false,
    isLoading: false,
    updateIsLoading: false
  });

  const [showUserId, setShowUserId] = useState("");
  const [stores, setStores] = useState([] as Store[]);

  useEffect(() => {
    if (!stores || stores.length === 0) {
      listAll().then(result => {
        if (isOk(result)) {
          setStores(result.value as Store[]);
        }
        updateRules();
      });
    }
  }, []);

  const updateRules = () => {
    getRules(outCome).then(result => {
      if (!isOk(result)) {
        setState({
          ...state,
          error: { type: "error", problem: result.details }
        });
      } else {
        let userRules = result.value as UserRule[];
        setState({
          ...state,
          rules: userRules.sort((a, b) => (a.order > b.order ? 1 : -1))
        });
      }
    });
  };

  const populateTestResults = () => {
    setState({ ...state, isLoading: true });
    getTestResult(state.rules).then(result => {
      if (!isOk(result)) {
        setState({
          ...state,
          error: { type: "error", problem: result.details },
          isLoading: false
        });
      } else {
        let testResults = result.value as UserRuleTestResult[];
        setState({
          ...state,
          testResults: testResults,
          isLoading: false
        });
      }
    });
  };

  const disabled = () => {
    for (let x of state.rules) {
      if (x.parameters.doorOpeningParameter) {
        if (
          !x.parameters.doorOpeningParameter?.storeIds ||
          x.parameters.doorOpeningParameter?.storeIds.length < 1 ||
          x.parameters.doorOpeningParameter?.openingsNoPurchase < 1 ||
          x.parameters.doorOpeningParameter?.ratio < 1
        ) {
          return true;
        }
      }
      if (
        x.parameters.failedPaymentParameter &&
        x.parameters.failedPaymentParameter?.limit < 1
      )
        return true;
    }
    return false;
  };

  const renderInputRow = (
    propertyName: string,
    value: any,
    style: any = {}
  ) => (
    <Grid container className={styles.userDetailsItem}>
      <Grid item xs={6} className={styles.detailsCell}>
        <div className={styles.detailsNameColumn}>{propertyName}</div>
      </Grid>
      <Grid item xs={6} className={styles.detailsCell}>
        <div className={styles.detailsDataColumn} style={style}>
          {value}
        </div>
      </Grid>
    </Grid>
  );

  const renderRule = (rule: UserRule) => {
    return (
      <Box pb={3} key={rule.name}>
        {rule.label && (
          <Typography
            variant={"subtitle2"}
            style={{ textTransform: "uppercase", marginTop: 0 }}
          >
            {rule.label}
          </Typography>
        )}
        {rule?.parameters?.doorOpeningParameter && (
          <>
            {renderInputRow(
              "Door openings, no purchase *",
              <TextField
                type={"number"}
                value={
                  rule?.parameters?.doorOpeningParameter?.openingsNoPurchase ??
                  ""
                }
                style={{ width: 50 }}
                error={
                  rule?.parameters?.doorOpeningParameter?.openingsNoPurchase < 1
                }
                label=""
                onChange={(e: any) => {
                  if (rule?.parameters?.doorOpeningParameter) {
                    rule.parameters.doorOpeningParameter.openingsNoPurchase =
                      e.target.value;
                    setState({
                      ...state,
                      rules: Object.assign([...state.rules], rule)
                    });
                  }
                }}
              />,
              { fontWeight: theme.typography.fontWeightRegular }
            )}
            {renderInputRow(
              "Purchase/door opening ratio *",
              <TextField
                type={"number"}
                value={rule?.parameters?.doorOpeningParameter?.ratio ?? ""}
                style={{ width: 70 }}
                error={rule?.parameters?.doorOpeningParameter?.ratio < 1}
                label=""
                InputProps={{
                  endAdornment: (
                    <InputAdornment style={{ marginLeft: 10 }} position="end">
                      %
                    </InputAdornment>
                  )
                }}
                onChange={(e: any) => {
                  if (rule?.parameters?.doorOpeningParameter) {
                    rule.parameters.doorOpeningParameter.ratio = e.target.value;
                    setState({
                      ...state,
                      rules: Object.assign([...state.rules], rule)
                    });
                  }
                }}
              />,
              { fontWeight: theme.typography.fontWeightRegular }
            )}
            {renderInputRow(
              "Apply in these stores *",
              <>
                <StoreMultiSelect
                  multiple={true}
                  storeList={stores.filter(s => s.state === "Opened")}
                  label={""}
                  error={
                    !rule?.parameters?.doorOpeningParameter?.storeIds ||
                    rule?.parameters?.doorOpeningParameter?.storeIds.length < 1
                  }
                  className={styles.storesSelector}
                  value={
                    rule?.parameters?.doorOpeningParameter?.storeIds ??
                    ([] as string[])
                  }
                  onSelected={s => {
                    if (rule?.parameters?.doorOpeningParameter) {
                      rule.parameters.doorOpeningParameter.storeIds = s.map(
                        x => x.id
                      );
                      setState({
                        ...state,
                        rules: Object.assign([...state.rules], rule)
                      });
                    }
                  }}
                />
                <FormControlLabel
                  style={{ marginLeft: 10 }}
                  control={
                    <Checkbox
                      checked={
                        stores.length > 0 &&
                        rule?.parameters?.doorOpeningParameter?.storeIds &&
                        rule?.parameters?.doorOpeningParameter?.storeIds
                          .length === stores.length
                      }
                      onChange={e => {
                        if (rule?.parameters?.doorOpeningParameter) {
                          rule.parameters.doorOpeningParameter.storeIds = e
                            .target.checked
                            ? stores.map(x => x.id)
                            : ([] as string[]);
                          setState({
                            ...state,
                            rules: Object.assign([...state.rules], rule)
                          });
                        }
                      }}
                    />
                  }
                  label="All"
                />
              </>,
              { fontWeight: theme.typography.fontWeightRegular }
            )}
          </>
        )}
        {rule?.parameters?.failedPaymentParameter &&
          renderInputRow(
            "Failed payments limit *",
            <TextField
              type={"number"}
              value={rule?.parameters?.failedPaymentParameter?.limit ?? ""}
              error={rule?.parameters?.failedPaymentParameter?.limit < 1}
              style={{ width: 50 }}
              label=""
              onChange={(e: any) => {
                if (rule?.parameters?.failedPaymentParameter) {
                  rule.parameters.failedPaymentParameter.limit = e.target.value;
                  setState({
                    ...state,
                    rules: Object.assign([...state.rules], rule)
                  });
                }
              }}
            />,
            { fontWeight: theme.typography.fontWeightRegular }
          )}
        {rule?.parameters?.stolenCardParameter &&
          renderInputRow(
            "Lock user with stolen card *",
            <ButtonGroup
              variant="contained"
              size={"small"}
              aria-label={t("common.tooltip.employee")}
              style={{ boxShadow: "none" }}
            >
              <Button
                variant={"outlined"}
                className={`${
                  rule?.parameters?.stolenCardParameter?.active
                    ? styles.employeeBtnSelected
                    : styles.employeeBtn
                }`}
                onClick={() => {
                  if (rule?.parameters?.stolenCardParameter) {
                    rule.parameters.stolenCardParameter.active = true;
                    setState({
                      ...state,
                      rules: Object.assign([...state.rules], rule)
                    });
                  }
                }}
              >
                Yes
              </Button>
              <Button
                variant={"outlined"}
                className={`${
                  !rule?.parameters?.stolenCardParameter?.active
                    ? styles.employeeBtnSelected
                    : styles.employeeBtn
                }`}
                onClick={() => {
                  if (rule?.parameters?.stolenCardParameter) {
                    rule.parameters.stolenCardParameter.active = false;
                    setState({
                      ...state,
                      rules: Object.assign([...state.rules], rule)
                    });
                  }
                }}
              >
                No
              </Button>
            </ButtonGroup>,
            { fontWeight: theme.typography.fontWeightRegular }
          )}
        {rule?.parameters?.failedPaymentParameter &&
          !rule?.parameters?.stolenCardParameter &&
          renderInputRow("", <></>, {
            fontWeight: theme.typography.fontWeightRegular
          })}
      </Box>
    );
  };

  return !state?.rules?.length ? (
    <div className={styles.spinnerContainer}>
      <CircularProgress />
    </div>
  ) : (
    <>
      <Fade in={true}>
        <Grid container spacing={2} direction={mobile ? "column" : "row"}>
          <Grid item xs={12}>
            <Card className={styles.fullHeight} key={outCome}>
              <CardContent>
                <Box sx={{ display: "flex" }} pb={0}>
                  <Box flexGrow={1}>
                    <div>
                      <Typography
                        variant={"subtitle2"}
                        color={"textSecondary"}
                        style={{ textTransform: "uppercase" }}
                      >
                        {title}
                      </Typography>
                    </div>
                  </Box>
                  <div>{icon}</div>
                </Box>

                {state.rules.map(x => renderRule(x))}

                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    style={{ width: 100 }}
                    size={"small"}
                    variant="outlined"
                    color="secondary"
                    onClick={() => updateRules()}
                  >
                    Reset
                  </Button>
                  <Button
                    style={{ width: 100, marginLeft: 10 }}
                    size={"small"}
                    variant="outlined"
                    color="primary"
                    onClick={() => populateTestResults()}
                  >
                    Test
                  </Button>
                  <Button
                    style={{ width: 100, marginLeft: 10 }}
                    size={"small"}
                    variant="contained"
                    color="primary"
                    disabled={disabled()}
                    onClick={async () => {
                      setState({ ...state, showConfirm: true });
                    }}
                  >
                    Save
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card className={styles.fullHeight} key={`test_${outCome}`}>
              <CardContent>
                <div style={{ position: "relative" }}>
                  <Box
                    display={"flex"}
                    alignItems={"center"}
                    position={!mobile ? "absolute" : "relative"}
                    zIndex={10}
                  >
                    <SummarizeOutlined
                      sx={{
                        fontSize: "3rem",
                        color: theme.palette.success.dark
                      }}
                    />
                    <Box pl={2}>
                      <Typography variant={"subtitle1"} color={"textSecondary"}>
                        Test result ({outCome})
                      </Typography>
                    </Box>
                  </Box>
                  <MaterialTable
                    title=""
                    isLoading={state.isLoading}
                    columns={[
                      {
                        title: "Id",
                        field: "userId",
                        hidden: true,
                        searchable: true
                      },
                      {
                        title: "Name",
                        field: "name",
                        render: (row: any) => (
                          <div style={{ minWidth: 180 }}>
                            <Link
                              to={"/users/" + row.userId}
                              onClick={() => setShowUserId(row.userId)}
                            >
                              {`${row.name ?? "..."}`}
                            </Link>
                            <Typography color="textSecondary" variant={"body2"}>
                              Open door: {row.doorOpeningCount}
                            </Typography>
                            <Typography color="textSecondary" variant={"body2"}>
                              Purchase: {row.purchaseCount}
                            </Typography>
                          </div>
                        )
                      },
                      {
                        title: "Openings no Purchase",
                        field: "doorOpeningWithoutMakingPurchaseCount"
                      },
                      {
                        title: "Ratio",
                        field: "ratio",
                        render: (row: any) => <>{row.ratio.toFixed(0)} %</>
                      }
                    ]}
                    data={state.testResults}
                    options={{
                      showTitle: false,
                      exportMenu: !mobile
                        ? [
                            {
                              label: t("common.export.exportPdf"),
                              exportFunc: (cols, data) =>
                                ExportPdf(cols, data, `Users`)
                            },
                            {
                              label: t("common.export.exportCsv"),
                              exportFunc: (cols, data) =>
                                ExportCsv(cols, data, `Users`)
                            }
                          ]
                        : [],
                      exportAllData: true
                    }}
                    components={{
                      Container: props => <Paper {...props} elevation={0} />
                    }}
                    localization={{
                      header: {
                        actions: ""
                      }
                    }}
                  />
                </div>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Fade>

      <Dialog
        onClose={() => setShowUserId("")}
        open={showUserId !== ""}
        fullWidth={true}
        fullScreen={mobile}
        maxWidth={"xl"}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle style={{ height: 50 }} disableTypography>
          <IconButton
            aria-label="close"
            className={styles.closeButton}
            onClick={() => setShowUserId("")}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent>
          <UserOverview inputUserId={showUserId} />
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        visible={state.showConfirm}
        titleText={`Update rules`}
        bodyText={`Are you sure you want to update the ${outCome} rules?`}
        confirmText={"Update"}
        confirmDisabled={state.updateIsLoading}
        cancelDisabled={state.updateIsLoading}
        onConfirm={async () => {
          setState({ ...state, updateIsLoading: true });
          const result = await upsertRules(state.rules);

          if (isOk(result)) {
            setState({
              ...state,
              error: {},
              upsertSuccess: true,
              showConfirm: false,
              updateIsLoading: false
            });
            return true;
          } else {
            setState({
              ...state,
              error: { type: "error", problem: result.details },
              updateIsLoading: false
            });
            return false;
          }
        }}
        onCancel={() => {
          setState({ ...state, error: {}, showConfirm: false });
        }}
      >
        {state.error?.problem && (
          <Alert severity="error">
            <AlertTitle>
              {state.error.problem.status}, {state.error.problem.title}
            </AlertTitle>
            {state.error.problem.detail}{" "}
            <strong>{state.error.problem.traceId}</strong>
          </Alert>
        )}
      </ConfirmDialog>

      <Snackbar
        open={state.upsertSuccess}
        autoHideDuration={3000}
        onClose={() => {
          setState({
            ...state,
            upsertSuccess: false
          });
        }}
      >
        <Alert severity={"success"}>
          {t("common.messages.successfullyUpdatedRules", { outCome })}
        </Alert>
      </Snackbar>
    </>
  );
};
